import { useQuery } from '@tanstack/react-query';
import { firestore } from '../../../firebase';
import { LowLevelAlert, FreezeAlert } from './FlowConfigHistory';

interface LowLevelLog {
  cm: number;
  createTime: Date;
}

export interface FlowDeviceLog {
  id: string; // Device identifier, assumed to be within the log data itself
  Te?: string; // Temperature (example, not used in current alarms)
  EE?: string; // Another example field
  l: string; // Liter water i alt
  m2?: string; // Another example field
  m3?: string; // Newer water counter alias
  cm: string; // Støvbinder niveauet
  createTime: Date;
}

type AlertLog = LowLevelLog | FlowDeviceLog;

// Type for the flow device config history entry
export interface FlowDeviceConfigHistory {
  config: LowLevelAlert | FreezeAlert;
  createTime: Date;
  type: 'belowLimit' | 'tempAlert';
  log: AlertLog;
}

// API function to get the config history for a device
const getFlowDeviceConfigHistory = async (
  configDocId: string
): Promise<(FlowDeviceConfigHistory & { id: string })[]> => {
  if (!configDocId) {
    throw new Error('Config document ID is required');
  }

  const snap = await firestore
    .collection('flowDeviceConfigs')
    .doc(configDocId)
    .collection('history')
    .orderBy('createTime', 'desc')
    .limit(10)
    .get();

  if (snap.empty) {
    return [];
  }

  return snap.docs.map((doc) => ({
    ...doc.data(),
    createTime: doc.data().createTime?.toDate() || new Date(),
    id: doc.id,
  })) as (FlowDeviceConfigHistory & { id: string })[];
};

// Custom hook for getting the flow device config history
export const useFlowConfigHistory = (configDocId: string | null) => {
  const query = useQuery({
    queryKey: ['flowDeviceConfigHistory', configDocId],
    queryFn: async (): Promise<(FlowDeviceConfigHistory & { id: string })[]> => {
      if (!configDocId) {
        return [];
      }
      return await getFlowDeviceConfigHistory(configDocId);
    },
    enabled: !!configDocId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return {
    ...query,
    history: query.data || [],
  };
};
